import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAction, useItem, useList } from '../api/queries'
import type { Alert, Plant } from '../api/types'
import { useUser } from '../auth/AuthContext'
import { DataTable, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { Card, PageHeader, Pagination, PillFilter, StatusBadge } from '../components/ui'
import { formatDate, humanize } from '../lib/format'
import { can } from '../lib/permissions'

const ALERT_STATUS: Record<string, string> = { open: 'Abierta', acknowledged: 'Reconocida', resolved: 'Resuelta' }

export function AlertsPage() {
  const [params] = useSearchParams()
  const plantParam = params.get('plant') ?? ''
  const plantId = /^\d+$/.test(plantParam) ? Number(plantParam) : undefined
  const asOf = params.get('as_of') || undefined
  const user = useUser()
  const [status, setStatus] = useState('open')
  const [pageNo, setPageNo] = useState(1)
  const plant = useItem<Plant>(plantId ? `/api/plants/${plantId}/` : null)
  const list = useList<Alert>('/api/alerts/', { plant: plantId, as_of: asOf, status, page: pageNo, ordering: '-last_seen_on' })
  const refresh = ['/api/alerts/', '/api/dashboard/']
  const ack = useAction<number>('post', (id) => `/api/alerts/${id}/acknowledge/`, refresh)
  const resolve = useAction<number>('post', (id) => `/api/alerts/${id}/resolve/`, refresh)
  const evaluate = useAction<void>('post', '/api/alerts/evaluate/', refresh)
  const columns: Column<Alert>[] = [
    { key: 's', header: 'Severidad', cell: (a) => <StatusBadge tone={a.severity === 'critical' ? 'danger' : a.severity === 'warning' ? 'warn' : 'info'} label={humanize(a.severity)} icon={a.severity === 'info' ? 'info' : 'triangle-alert'} /> },
    { key: 'm', header: 'Alerta', cell: (a) => <>{a.message}<div className="muted">{humanize(a.kind)}{a.equipment_code ? <> · <span className="mono">{a.equipment_code}</span></> : null}</div></> },
    { key: 'st', header: 'Estado', cell: (a) => ALERT_STATUS[a.status] ?? humanize(a.status) },
    { key: 'd', header: 'Última vez vista', cell: (a) => formatDate(a.last_seen_on), priority: 'low' },
    { key: 'x', header: '', cell: (a) => (
      <span className="row">
        {a.status === 'open' && can(user.role, 'alert.acknowledge') ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => ack.mutate(a.id)}>Reconocer</button> : null}
        {a.status !== 'resolved' && can(user.role, 'alert.resolve') ? <button type="button" className="btn btn--ghost btn--sm" onClick={() => resolve.mutate(a.id)}>Resolver</button> : null}
      </span>) },
  ]
  return (
    <div className="page">
      <PageHeader title={plantId ? `Alertas de ${plant.data?.name ?? 'la planta'}` : 'Alertas'} subtitle="Basadas en reglas: se evalúan después de cada importación e inspección enviada" actions={can(user.role, 'alert.evaluate') ? <button type="button" className="btn btn--ghost" onClick={() => evaluate.mutate()}><Icon name="refresh" size={16} /> Reevaluar</button> : undefined} />
      <Card>
        <div className="pills" role="group" aria-label="Estado">{['open', 'acknowledged', 'resolved', ''].map((s) => <PillFilter key={s} active={status === s} onClick={() => { setStatus(s); setPageNo(1) }}>{s ? (ALERT_STATUS[s] ?? humanize(s)) : 'Todas'}</PillFilter>)}</div>
        <DataTable caption="Alertas" columns={columns} rows={list.data?.results} loading={list.isPending} error={list.error} onRetry={() => void list.refetch()} rowKey={(a) => a.id} emptyTitle="No hay alertas" />
        {list.data ? <Pagination page={pageNo} pageSize={50} count={list.data.count} onPage={setPageNo} /> : null}
      </Card>
    </div>
  )
}
